import type { FlagValue } from './api';

/** Lecture défensive du rapport d'agent stocké : le JSON vient d'une instance, rien n'y est présumé. */
export interface ReportView {
  dshVersion: string | null;
  agentVersion: string | null;
  imageVersion: string | null;
  plugins: { package: string; version: string; enabled: boolean; phase: string; error: string | null }[];
  recentErrors: { at: string | null; message: string; scope: string | null }[];
  effectiveFlags: Record<string, FlagValue>;
  drift: {
    detected: boolean;
    items: { kind: string; name: string; expected: string | null; actual: string | null }[];
  };
  restartPending: boolean;
  lastActivityAt: string | null;
  lastApplyError: string | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const list = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? v.filter(isRecord) : []);

export function viewReport(raw: Record<string, unknown>): ReportView {
  const flags: Record<string, FlagValue> = {};
  if (isRecord(raw.effectiveFlags)) {
    for (const [k, v] of Object.entries(raw.effectiveFlags)) {
      if (typeof v === 'boolean' || typeof v === 'string' || typeof v === 'number') flags[k] = v;
    }
  }
  const drift = isRecord(raw.drift) ? raw.drift : {};
  const items = list(drift.items).map((i) => ({
    kind: str(i.kind) ?? '?',
    name: str(i.name) ?? '?',
    expected: str(i.expected),
    actual: str(i.actual),
  }));
  return {
    dshVersion: str(raw.dshVersion),
    agentVersion: str(raw.agentVersion),
    imageVersion: str(raw.imageVersion),
    plugins: list(raw.plugins).map((p) => ({
      package: str(p.package) ?? '?',
      version: str(p.version) ?? '?',
      enabled: p.enabled === true,
      phase: str(p.phase) ?? '—',
      error: str(p.error),
    })),
    recentErrors: list(raw.recentErrors).map((e) => ({
      at: str(e.at),
      message: str(e.message) ?? '?',
      scope: str(e.scope),
    })),
    effectiveFlags: flags,
    drift: { detected: drift.detected === true || items.length > 0, items },
    restartPending: raw.restartPending === true,
    lastActivityAt: str(raw.lastActivityAt),
    lastApplyError: str(raw.lastApplyError),
  };
}
